import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';  // same-origin by default

// Configure axios defaults
axios.defaults.withCredentials = true;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Algo deu errado. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const { data } = await axios.get(`${API_URL}/api/auth/me`, { headers });
      // Normalize id/_id so role-gated UI works in any code path
      setUser({ ...data, id: data.id || data._id, _id: data._id || data.id });
    } catch (err) {
      localStorage.removeItem('token');
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from old OAuth callback (Emergent), skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // New Google OAuth 2.0 flow: backend sets cookies and redirects with ?google_auth=success
    if (window.location.search?.includes('google_auth=success')) {
      // Clean the URL params
      window.history.replaceState({}, '', window.location.pathname);
      // Now that cookies are set, check auth to establish the session
      checkAuth();
      return;
    }

    // Also handle Google OAuth errors in the URL
    if (window.location.search?.includes('google_error=')) {
      // Let LoginPage handle the error display, just clean the URL
      setLoading(false);
      return;
    }

    checkAuth();
  }, [checkAuth]);

  /* MONITOR (Fase 2) — o login passa a trazer `sou_monitor`, mas isto mantem
     a tela correta mesmo contra um backend antigo: confirma o perfil no
     `/auth/me` logo depois de entrar. Nao usa o `checkAuth` de proposito:
     se a consulta falhar, o usuario do login e MANTIDO (nao desloga). */
  const confirmarVinculo = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser((atual) => ({
        ...(atual || {}), ...data,
        id: data.id || data._id, _id: data._id || data.id,
      }));
    } catch (err) {
      /* mantem o usuario que veio do login */
    }
  };

  const login = async (email, password) => {
    setError(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      localStorage.setItem('token', data.token);
      // Normalize id/_id (login returns `id`, /auth/me returns `_id`)
      setUser({ ...data, id: data.id || data._id, _id: data._id || data.id });
      confirmarVinculo();  // descobre o vinculo de Monitor sem exigir F5
      return data;
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail);
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const register = async (name, email, password, role = 'athlete') => {
    setError(null);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/register`, { name, email, password, role });
      localStorage.setItem('token', data.token);
      setUser({ ...data, id: data.id || data._id, _id: data._id || data.id });
      confirmarVinculo();  // descobre o vinculo de Monitor sem exigir F5
      return data;
    } catch (err) {
      const errorMsg = formatApiErrorDetail(err.response?.data?.detail);
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (err) {
      // Ignore logout errors
    } finally {
      localStorage.removeItem('token');
      setUser(false);
    }
  };

  /* MONITOR (Fase 1) — o Monitor NÃO é um papel novo: continua sendo `athlete`
     com um VÍNCULO ATIVO numa equipe. O `/api/auth/me` já devolve isso pronto
     (`sou_monitor` + `monitor`), então aqui só REPASSAMOS os campos — nenhuma
     requisição nova é feita e nada do que já existia muda. A leitura tolera o
     formato antigo (sem os campos) e a ausência de sessão (`user` pode ser
     `false`, não só `null`). */
  const eMonitor = !!(user && (user.sou_monitor === true || user.monitor));
  const monitor = (user && user.monitor) || null;

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'super_admin',
    isFederation: user?.role === 'federation',
    isProfessor: user?.role === 'professor',
    isStaff: ['staff', 'central_desk'].includes(user?.role),
    // Monitor ativo + o vínculo (equipe/professor), quando houver
    eMonitor,
    monitor,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
